import { BackButton } from "@/components/BackButton";
import { PageTransition } from "@/components/PageTransition";
import { QrContent } from "@/components/QrContent";
import { Button } from "@photo-booth/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@photo-booth/ui/dialog";
import { Input } from "@photo-booth/ui/input";
import { useCouponValidation } from "@/hooks/useCouponValidation";
import { useQrPayment } from "@/hooks/useQrPayment";
import {
  isCouponPaymentEnabled,
  isQrPaymentEnabled,
} from "@/lib/configuration/payment";
import { useSEO } from "@/lib/seo/useSEO";
import { ArrowRightIcon, Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { usePollPaymentStatus } from "@/hooks/api/usePollPaymentStatus";
import { toast } from "sonner";
import { track, trackError } from "@/lib/analytics/track";
import { usePaymentStore } from "@/lib/store/paymentStore";

export const PaymentPage: React.FC = () => {
  const { qr, error, isPaymentVerifying, handlePaymentVerification, isGeneratingQr } =
    useQrPayment();
  const submitRef = useRef<boolean>(false);
  const { setPaymentPayload, resetQr } = usePaymentStore();
  const {
    couponCode,
    setCouponCode,
    isValidatingCoupon,
    handleValidateCoupon,
  } = useCouponValidation();
  const navigate = useNavigate();

  // Start polling when QR exists
  const { data: paymentStatus } = usePollPaymentStatus(qr?.transactionId || "");

  useSEO({
    title: "Payment",
    description: "Payment page",
    keywords: "payment, page",
  })

  useEffect(() => {
    if (isQrPaymentEnabled() && !qr && !submitRef.current && !isGeneratingQr) {
      navigate("/frame-detail");
    }
  }, [qr, navigate, isGeneratingQr]);

  // Handle payment status changes
  useEffect(() => {
    if (!paymentStatus) return;
    
    if (paymentStatus.status === "VALIDATED") {
      submitRef.current = true;
      // Payment successful

      setPaymentPayload({
        token: paymentStatus.token,
        transactionId: qr?.transactionId || "",
        childFrameId: paymentStatus.childFrameId,
      });
      track("payment_verify_success", {
        token_present: Boolean(paymentStatus?.token),
        child_frame_id: paymentStatus.childFrameId,
      });
      navigate("/camera");
      resetQr();
    } else if (paymentStatus.status === "REJECTED") {
      // Payment rejected
      const errorMessage = paymentStatus.reason || "Payment was rejected";
      toast.error(errorMessage);
      track("payment_verify_error", { message: errorMessage });
      trackError({
        type: "payment",
        message: errorMessage,
        source: "manual",
      });
      resetQr();
      navigate("/frame-detail");
    }
  }, [paymentStatus, qr?.transactionId, setPaymentPayload, navigate, resetQr]);

  const qrCodeComponent = isQrPaymentEnabled() ? (
    <QrContent qr={qr || null} error={error} />
  ) : (
    <div className="text-vintage-black">
      We're getting closer, see you soon!
    </div>
  );

  const couponCodeComponent = isCouponPaymentEnabled() && (
    <Dialog>
      <DialogTrigger asChild className="self-start ps-8">
        <span className="text-sm text-gray-500 cursor-pointer opacity-70 text-left">
            have a coupon code?
        </span>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Apply Coupon</DialogTitle>
        </DialogHeader>
        <div className="flex flex-row justify-center items-center gap-2 mt-4">
          <Input
            type="text"
            placeholder="Enter coupon code"
            className="font-family-kanit"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
          />
          <Button
            type="button"
            onClick={handleValidateCoupon}
            disabled={isValidatingCoupon}
          >
            Apply Coupon
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  const handleSubmit = () => {
    submitRef.current = true;
    handlePaymentVerification();
  }

  return (
    <PageTransition>
      <BackButton prevUrl="/frame-detail" />
      <div className="px-4 py-4 h-screen-safe-area overflow-y-hidden">
        <div className="flex flex-col items-center">
          <h1 className="text-4xl text-vintage-red"> Make a Payment</h1>

          <div className="mt-4 min-h-[60vh] flex flex-col items-center justify-center w-full">
            {qrCodeComponent}
          </div>
          {couponCodeComponent}
        </div>
        <div className="flex justify-end mt-4">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isPaymentVerifying}
          >
            {isPaymentVerifying ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowRightIcon className="w-4 h-4" />
            )}
            Verify Payment
          </Button>
        </div>
      </div>
    </PageTransition>
  );
};
